import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableSortLabel,
  TableRow,
  Typography,
} from '@mui/material';
import moment from 'moment';
import toast from 'react-hot-toast';
import type { Oth360UsersSortBy, Pagination, SortDir } from 'src/api/oth360';
import styles from './UsersTable.module.scss';

export type User360Evaluation = {
  id: string;
  subjectName: string;
  subjectEmail: string;
  leaderLink: string;
  employeeLink: string;
  leaderDone: boolean | null;
  answersCount: number | null;
  createdAt: string;
};

interface UsersTableProps {
  users: User360Evaluation[];
  onEditUser: (user: User360Evaluation) => void;
  onDeleteUser: (user: User360Evaluation) => void;
  onAddUser?: () => void;
  deletingIds?: Record<string, boolean>;
  pagination?: Pagination | null;
  isLoading?: boolean;
  onPageChange?: (nextPage: number) => void;
  onPageSizeChange?: (nextPageSize: number) => void;
  sortBy?: Oth360UsersSortBy;
  sortDir?: SortDir;
  onRequestSort?: (next: Oth360UsersSortBy) => void;
}

const UsersTable = ({
  users,
  onAddUser,
  onDeleteUser,
  deletingIds,
  pagination,
  isLoading,
  onPageChange,
  onPageSizeChange,
  sortBy = 'createdAt',
  sortDir = 'desc',
  onRequestSort,
}: UsersTableProps) => {
  const getMailToLink = (email: string, name: string) => {
    const subject = `360 Evaluation - ${name}`;
    return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}`;
  };

  const handleCopyLink = (link: string) => {
    navigator.clipboard
      .writeText(link)
      .then(() => toast.success('Link copied to clipboard!'))
      .catch(() => toast.error('Failed to copy link.'));
  };

  return (
    <TableContainer component={Paper} className={styles.tableContainer}>
      {onAddUser && (
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            minHeight: 60,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              visibility: isLoading === true ? 'visible' : 'hidden',
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Loading…
            </Typography>
          </Box>

          <Button
            type="button"
            variant="contained"
            sx={{
              textTransform: 'none',
              fontSize: '18px',
              width: '232px',
            }}
            size="large"
            onClick={onAddUser}
          >
            Add user
          </Button>
        </Box>
      )}

      <Table aria-label="360 evaluation users table">
        <TableHead>
          <TableRow>
            <TableCell
              align="center"
              sortDirection={sortBy === 'subjectName' ? sortDir : false}
            >
              <TableSortLabel
                active={sortBy === 'subjectName'}
                direction={sortBy === 'subjectName' ? sortDir : 'asc'}
                onClick={() => onRequestSort?.('subjectName')}
              >
                User
              </TableSortLabel>
            </TableCell>

            <TableCell
              align="center"
              sortDirection={sortBy === 'subjectEmail' ? sortDir : false}
            >
              <TableSortLabel
                active={sortBy === 'subjectEmail'}
                direction={sortBy === 'subjectEmail' ? sortDir : 'asc'}
                onClick={() => onRequestSort?.('subjectEmail')}
              >
                User Email
              </TableSortLabel>
            </TableCell>
            <TableCell align="center">Leader Link</TableCell>
            <TableCell align="center">Employee Link</TableCell>
            <TableCell align="center">Leader done</TableCell>
            <TableCell align="center">Answers</TableCell>
            <TableCell
              align="center"
              sortDirection={sortBy === 'createdAt' ? sortDir : false}
            >
              <TableSortLabel
                active={sortBy === 'createdAt'}
                direction={sortBy === 'createdAt' ? sortDir : 'desc'}
                onClick={() => onRequestSort?.('createdAt')}
              >
                Created
              </TableSortLabel>
            </TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {users.map((user) => {
            const isDeleting = deletingIds?.[user.id] === true;

            return (
              <TableRow key={user.id} className={styles.tableRow}>
                <TableCell align="center">{user.subjectName}</TableCell>
                <TableCell align="center">
                  <a href={getMailToLink(user.subjectEmail, user.subjectName)}>
                    {user.subjectEmail}
                  </a>
                </TableCell>

                <TableCell align="center">
                  <div className={styles.linkCell}>
                    <div className={styles.linkActions}>
                      <Button
                        variant="outlined"
                        onClick={() => handleCopyLink(user.leaderLink)}
                        startIcon={<ContentCopyIcon />}
                        disabled={isDeleting}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                </TableCell>

                <TableCell align="center">
                  <div className={styles.linkCell}>
                    <div className={styles.linkActions}>
                      <Button
                        variant="outlined"
                        onClick={() => handleCopyLink(user.employeeLink)}
                        startIcon={<ContentCopyIcon />}
                        disabled={isDeleting}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                </TableCell>

                <TableCell align="center">
                  {user.leaderDone === true ? (
                    <CheckCircleOutlinedIcon
                      fontSize="small"
                      sx={{ color: '#16a34a' }}
                      titleAccess="Done"
                    />
                  ) : user.leaderDone === false ? (
                    <CancelOutlinedIcon
                      fontSize="small"
                      sx={{ color: '#dc2626' }}
                      titleAccess="Not done"
                    />
                  ) : (
                    <HelpOutlineOutlinedIcon
                      fontSize="small"
                      sx={{ color: '#6b7280' }}
                      titleAccess="Unknown"
                    />
                  )}
                </TableCell>

                <TableCell align="center">
                  {user.answersCount === null ? '—' : user.answersCount}
                </TableCell>

                <TableCell align="center">
                  {moment(user.createdAt).format('DD.MM.YYYY')}
                </TableCell>

                <TableCell align="center">
                  <div className={styles.actions}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => onDeleteUser(user)}
                      disabled={isDeleting}
                      startIcon={
                        isDeleting ? <CircularProgress size={16} /> : undefined
                      }
                    >
                      {isDeleting ? 'Deleting…' : 'Delete'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {pagination && (
        <TablePagination
          component="div"
          count={pagination.totalCount}
          page={Math.max(0, pagination.currentPage - 1)}
          rowsPerPage={pagination.pageSize}
          rowsPerPageOptions={[20, 50, 100]}
          onPageChange={(_, nextPageIndex) => onPageChange?.(nextPageIndex + 1)}
          onRowsPerPageChange={(e) => {
            const next = Number(e.target.value);
            if (Number.isFinite(next) && next > 0) {
              onPageSizeChange?.(next);
            }
          }}
          labelRowsPerPage="Rows per page"
          backIconButtonProps={{
            disabled: isLoading === true || pagination.currentPage <= 1,
          }}
          nextIconButtonProps={{
            disabled:
              isLoading === true ||
              pagination.currentPage >= pagination.totalPages,
          }}
        />
      )}
    </TableContainer>
  );
};

export default UsersTable;
